import axios from 'axios';
import { store } from '../store/store';
import slugify from 'slugify';
import { defaultValForFieldType } from './utils';
import generics from './generics';
import initialStates from './buildInitialState';
import { v4 as uuid } from 'uuid';
import * as Sentry from '@sentry/react';

export const buildFormState = (form, dataAPI) => {
	const fieldTypesWithValues = ['textfield', 'textarea', 'select', 'email', 'checkbox', 'hidden', 'telephone'];

	const findAPIVal = (name, key) => {
		const parts = name.split(':');
		const type = parts[0];
		const field = parts[1] === 'phase_number' ? 'phase' : parts[1];
		const list = parts[2];
		const listVal = parts[3];
		const listField = parts[4];

		let ret = '';

		switch (type) {
			case 'phase': {
				const apiVal = dataAPI.getPhaseValue(field);
				if (apiVal && list) {
					const _dataList = dataAPI.getDataList(list);
					ret = _dataList.find(listItem => listItem[listVal] === apiVal)?.[listField];
				} else if (apiVal) {
					ret = apiVal;
				}
				break;
			}
			case 'client': {
				const idField = key.split('_').shift() + '_id';
				const clientId = dataAPI.getPhaseValue(idField);
				ret = window.clientList.find(({ client_id }) => client_id === clientId)?.[field];
				break;
			}
			case 'contact': {
				const idField = key.split('_').shift() + '_id';
				const contactId = dataAPI.getPhaseValue(idField);
				ret = dataAPI.getContactField(contactId, field);
				break;
			}
		}

		return ret;
	};

	const getInitialVal = (field, key, type) => {
		let initialVal;

		if (Object.prototype.hasOwnProperty.call(field, '#default_value')) {
			const defaultVal = field['#default_value'];
			if (typeof defaultVal === 'string' && defaultVal.indexOf(':') !== -1) {
				const val = findAPIVal(defaultVal, key);
				initialVal = val ?? defaultValForFieldType(type);
			} else {
				initialVal = defaultVal;
			}
		} else {
			initialVal = defaultValForFieldType(type);
		}

		return initialVal;
	};

	const parseField = (field, key, fields = {}, parent) => {
		const childFields = Object.keys(field).filter(key => key[0] !== '#');

		const type = field['#type'];
		const repeater = field['#repeater'];
		const save = field['#save'];

		if (!type || save === false) {
			return fields;
		}

		const hasVal = fieldTypesWithValues.find(fieldType => fieldType === type);

		if (key && hasVal) {
			fields[key] = getInitialVal(field, key, type);
		}

		if (repeater) {
			fields[key] = [];
		}

		if (type === 'webform_section') {
			if (form.id === 'loadout_list') {
				fields[key] = {
					categories: {}
				};

				if (Object.prototype.hasOwnProperty.call(field, '#addons')) {
					fields[key].addons = [];
				}

				if (Object.prototype.hasOwnProperty.call(field, '#unit')) {
					fields[key].unit = field['#unit'] ?? '';
				}
			}
		}

		if (type === 'webform_element') {
			if (key === 'time_item_defaults') {
				for (const prop in generics.time_item_defaults) {
					fields[prop] = generics.time_item_defaults[prop].map(time_item => {
						time_item.key = `${time_item.code}-1`;
						time_item.phase_number = fields.phase_number;
						return time_item;
					});
				}
			}

			if (key === 'units_defaults') {
				const phaseNumber = dataAPI.getPhaseValue('phase');
				fields.units = {
					phase_number: phaseNumber,
					task: ''
				};
			}

			if (key === 'per_diem_defaults') {
				const phaseNumber = dataAPI.getPhaseValue('phase') || '';
				const projectNumber = dataAPI.getPhaseValue('project_number') || '';

				const prefix = projectNumber.split('-')?.shift();
				const pdDefaults = prefix ? window.appData.per_diem_settings[prefix] || {} : {};
				fields.per_diems = {};
				fields.per_diems.breakfast_cost = pdDefaults.breakfast ?? '9.00';
				fields.per_diems.lunch_cost = pdDefaults.lunch ?? '11.00';
				fields.per_diems.dinner_cost = pdDefaults.dinner ?? '20.00';
				fields.per_diems.phase_number = phaseNumber;
				fields.per_diems.task = '';
				fields.per_diems.overnight = false;
			}
		}

		if (type === 'item') {
			const measure = field['#measure'] ?? false;
			const unit = field['#unit'] ?? '';
			const billable = field['#billable'] ?? false;
			const categories = field['#categories'] ?? [];
			const select = field['#select'] ?? false;
			const input_list = field['#input_list'] ?? false;

			categories.forEach(cat => {
				const slug = slugify(cat, {
					replacement: '_',
					lower: true,
					remove: /[*+~.()'"!:@]/g
				});

				if (!Object.prototype.hasOwnProperty.call(fields[parent].categories, slug)) {
					fields[parent].categories[slug] = {
						name: cat,
						checked: false,
						items: [key]
					};
				} else {
					fields[parent].categories[slug].items.push(key);
				}
			});

			const part_one = { checked: false };
			const part_two = { checked: false };

			if (measure) {
				part_one.measure = '';
				part_two.measure = '';
			}

			fields[parent][key] = {
				unit,
				billable,
				part_one,
				part_two
			};

			if (select) {
				if (select.multi) {
					fields[parent][key].items = [];
				} else {
					fields[parent][key].selection = select.default || '';
				}
			}

			if (input_list) {
				fields[parent][key].items = [];
			}
		}

		if (childFields && !repeater) {
			childFields.forEach(childField => parseField(field[childField], childField, fields, key));
		}

		return fields;
	};

	return Object.prototype.hasOwnProperty.call(initialStates, form.id)
		? initialStates[form.id]
		: parseField(form.elements.form_root);
};

const parseSerialized = data => (typeof data === 'object' ? data : JSON.parse(data));

export default async (dataAPI, tag, packets, phase, template) => {
	const username = localStorage.getItem('username');
	const creationId = uuid();

	const supervisorPacket = phase ? packets.find(({ packet_tag }) => packet_tag === 'supervisor') : null;

	const { webforms } = store.getState();

	const formDefinitions = webforms.forms
		?.filter(({ tags }) => tags.find(({ name }) => name === tag))
		?.sort((a, b) => {
			const orderA = a.tags.find(({ name }) => name === tag).order;
			const orderB = b.tags.find(({ name }) => name === tag).order;
			return orderA - orderB;
		});

	const formsData = formDefinitions?.map(formDefinition => {
		const parentForm = supervisorPacket?.packet_forms?.find(({ form_id }) => form_id === formDefinition.id);
		const parentData = parentForm && parseSerialized(parentForm.form_data, formDefinition.id);
		const templateForm = template?.packet_forms?.find(({ form_id }) => form_id === formDefinition.id);
		const templateData = templateForm && parseSerialized(templateForm.form_data, formDefinition.id);
		const templateDefinition = templateForm && parseSerialized(templateForm.form_definition, formDefinition.id);

		let formState;

		if (formDefinition.tags.find(({ name }) => name === 'supervisor') && templateData) {
			formState = templateData;
			formDefinition = templateDefinition;
		} else if (parentForm) {
			formState = parentData;
		} else {
			formState = buildFormState(formDefinition, dataAPI);
		}

		return {
			form_id: formDefinition.id,
			form_data: formState,
			form_definition: formDefinition
		};
	});

	const packet = {
		phase_nid: phase?.nid,
		project_number: phase?.project_number,
		phase_number: phase?.phase,
		project_short_name: phase?.project_short_name,
		packet_tag: tag,
		packet_forms: formsData,
		creation_id: creationId,
		created_by: username,
		unassigned: (!phase).toString(),
		platform: 'web'
	};

	return axios
		.post('/forms_rest_api/packet', packet, { headers: { 'Content-Type': 'application/json' } })
		.then(({ data }) => ({
			...data,
			packet_nid: parseInt(data.packet_nid),
			created: `${data.created}`,
			packet_attachments: {
				photos: [],
				sketches: [],
				comments: []
			}
		}))
		.catch(error => {
			Sentry.captureException(error);
			throw error;
		});
};
